import { useState } from "react";
import { api } from "@/lib/api";
import { useRouter } from "@/lib/router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, X, DollarSign } from "lucide-react";

const CURRENT_USER_ID = "u1";

export function CreateBounty() {
	const { navigate } = useRouter();
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [bountyAmount, setBountyAmount] = useState("");
	const [deadline, setDeadline] = useState("");
	const [requirements, setRequirements] = useState<string[]>([""]);
	const [tagInput, setTagInput] = useState("");
	const [tags, setTags] = useState<string[]>([]);
	const [submitting, setSubmitting] = useState(false);

	function addRequirement() {
		setRequirements([...requirements, ""]);
	}

	function updateRequirement(index: number, value: string) {
		const updated = [...requirements];
		updated[index] = value;
		setRequirements(updated);
	}

	function removeRequirement(index: number) {
		setRequirements(requirements.filter((_, i) => i !== index));
	}

	function addTag() {
		const tag = tagInput.trim();
		if (tag && !tags.includes(tag)) {
			setTags([...tags, tag]);
			setTagInput("");
		}
	}

	function removeTag(tag: string) {
		setTags(tags.filter((t) => t !== tag));
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setSubmitting(true);
		const bounty = await api.createBounty({
			title,
			description,
			requirements: requirements.filter(Boolean),
			bountyAmount: parseInt(bountyAmount, 10),
			deadline: new Date(deadline).toISOString(),
			tags,
			posterId: CURRENT_USER_ID,
		});
		navigate(`/bounty/${bounty.id}`);
	}

	const isValid = title && description && bountyAmount && deadline;

	return (
		<div className="container mx-auto px-4 py-8 max-w-2xl">
			<button
				onClick={() => navigate("/")}
				className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
			>
				<ArrowLeft className="h-4 w-4" />
				Back to bounties
			</button>

			<h1 className="text-2xl font-bold tracking-tight mb-2">Post a Bounty</h1>
			<p className="text-muted-foreground mb-8">
				Describe the problem you need solved. Set a bounty. Get working solutions.
			</p>

			<form onSubmit={handleSubmit}>
				<Card>
					<CardContent className="pt-6 space-y-6">
						<div>
							<Label>Title</Label>
							<Input
								placeholder="e.g., Build a CLI tool for batch image resizing"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								className="mt-1.5"
							/>
						</div>

						<div>
							<Label>Description</Label>
							<Textarea
								placeholder="Describe the problem in detail. What exactly do you need? What are the constraints?"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								rows={5}
								className="mt-1.5"
							/>
						</div>

						<div>
							<Label>Requirements</Label>
							<div className="space-y-2 mt-1.5">
								{requirements.map((req, i) => (
									<div key={i} className="flex items-center gap-2">
										<Input
											placeholder={`Requirement ${i + 1}`}
											value={req}
											onChange={(e) => updateRequirement(i, e.target.value)}
										/>
										{requirements.length > 1 && (
											<Button
												type="button"
												variant="ghost"
												size="icon"
												onClick={() => removeRequirement(i)}
												className="shrink-0"
											>
												<X className="h-4 w-4" />
											</Button>
										)}
									</div>
								))}
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={addRequirement}
									className="gap-1"
								>
									<Plus className="h-3 w-3" />
									Add Requirement
								</Button>
							</div>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div>
								<Label>Bounty Amount ($)</Label>
								<div className="relative mt-1.5">
									<DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
									<Input
										type="number"
										placeholder="500"
										min={1}
										value={bountyAmount}
										onChange={(e) => setBountyAmount(e.target.value)}
										className="pl-9"
									/>
								</div>
							</div>
							<div>
								<Label>Deadline</Label>
								<Input
									type="date"
									value={deadline}
									onChange={(e) => setDeadline(e.target.value)}
									className="mt-1.5"
									min={new Date().toISOString().split("T")[0]}
								/>
							</div>
						</div>

						<div>
							<Label>Tags</Label>
							<div className="flex items-center gap-2 mt-1.5">
								<Input
									placeholder="Add a tag..."
									value={tagInput}
									onChange={(e) => setTagInput(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											e.preventDefault();
											addTag();
										}
									}}
								/>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={addTag}
								>
									Add
								</Button>
							</div>
							{tags.length > 0 && (
								<div className="flex flex-wrap gap-2 mt-2">
									{tags.map((tag) => (
										<span
											key={tag}
											className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs font-medium"
										>
											{tag}
											<button
												type="button"
												onClick={() => removeTag(tag)}
												className="text-muted-foreground hover:text-foreground"
											>
												<X className="h-3 w-3" />
											</button>
										</span>
									))}
								</div>
							)}
						</div>

						<Button
							type="submit"
							className="w-full"
							disabled={!isValid || submitting}
						>
							{submitting ? "Posting..." : "Post Bounty"}
						</Button>
					</CardContent>
				</Card>
			</form>
		</div>
	);
}
